/**
 * Profile Rail Card
 *
 * v3 rule: the user profile lives at the BOTTOM of the sidebar rail, not in
 * the app bar (the app bar carries only the breadcrumb + env pill — see
 * CustomAppBar). Translucent white overlay card on the solid Deep Teal rail
 * (avatar, name + role), opening a menu that keeps the same content the
 * previous app-bar user menu (MyUserMenu) showed: the live session-expiry
 * countdown and the Profile / Logout actions. Collapsed rail shows just the
 * avatar.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslate, useGetIdentity, useLogout } from 'react-admin';
import {
  Avatar,
  Box,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccessTime as TimeIcon,
  UnfoldMoreRounded as UnfoldMoreIcon,
} from '@mui/icons-material';
import { decodeJWT } from '@/utils/auth';
import { AUTH_CONFIG } from '@/config/constants';

const getInitials = (fullName?: string | null): string => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  const initials = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0].slice(0, 2);
  return initials.toUpperCase();
};

interface ProfileRailCardProps {
  collapsed?: boolean;
}

export const ProfileRailCard: React.FC<ProfileRailCardProps> = ({ collapsed = false }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const logout = useLogout();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  const formatTimeRemaining = useCallback((seconds: number): string => {
    if (seconds <= 0) return translate('auth.errors.session_expired');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  }, [translate]);

  const calculateTimeRemaining = useCallback(() => {
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    if (!token) {
      setTimeRemaining('');
      return;
    }
    try {
      const decodedToken = decodeJWT(token);
      if (!decodedToken || !decodedToken.exp) {
        setTimeRemaining('');
        return;
      }
      const currentTime = Math.floor(Date.now() / 1000);
      const timeLeft = decodedToken.exp - currentTime;
      if (timeLeft <= 0) {
        setTimeRemaining(translate('auth.errors.session_expired'));
        setIsExpiringSoon(true);
        return;
      }
      setIsExpiringSoon(timeLeft <= 300);
      setTimeRemaining(formatTimeRemaining(timeLeft));
    } catch {
      setTimeRemaining('');
    }
  }, [formatTimeRemaining, translate]);

  useEffect(() => {
    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeRemaining]);

  const handleLogout = () => {
    setAnchor(null);
    logout();
  };

  const userName = identity?.fullName || 'User';
  const userRole = (identity as any)?.role || 'User';
  const roleLabel = translate(`auth.roles.${userRole}`, { _: userRole });

  const avatar = (
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: '0.75rem',
        fontWeight: 700,
        // Teal-ink disc with white initials (the app's CTA fill), a touch
        // darker than the rail so it reads as a distinct avatar.
        backgroundColor: theme.custom.ctaFill,
        color: '#FFFFFF',
      }}
    >
      {getInitials(userName)}
    </Avatar>
  );

  return (
    <>
      {collapsed ? (
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            mt: 1,
            display: 'flex',
            justifyContent: 'center',
            padding: '6px 0',
            borderRadius: '8px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
          }}
        >
          {avatar}
        </Box>
      ) : (
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '8px',
            // Translucent white overlay card, not an opaque white block —
            // stays part of the same solid-teal rail language.
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: '8px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
          }}
        >
          {avatar}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: '12.5px', fontWeight: 600, color: '#FFFFFF' }}>
              {userName}
            </Typography>
            <Typography noWrap sx={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.7)' }}>
              {roleLabel}
            </Typography>
          </Box>
          <UnfoldMoreIcon sx={{ fontSize: '18px', color: 'rgba(255,255,255,0.7)' }} />
        </Box>
      )}

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {timeRemaining && (
          <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'center' }}>
            <Chip
              icon={<TimeIcon />}
              label={timeRemaining}
              size="small"
              color={isExpiringSoon ? 'warning' : 'success'}
              sx={{ fontWeight: 500, '& .MuiChip-icon': { fontSize: 16 } }}
            />
          </Box>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            window.location.hash = '#/profile';
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('user.profile', { _: 'Perfil' })}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('ra.auth.logout', { _: 'Logout' })}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProfileRailCard;
